import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthorizerResponseDto } from '../../model/authorizer-response-dto';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  phone: string;
  password: string;
  type?: string;
  status?: string;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  authorizer: AuthorizerResponseDto;
  mustChangePassword: boolean;
}

export interface ApiResponse<T> {
  isSuccess: boolean;
  message: string;
  data: T | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpClient = inject(HttpClient);
  private router = inject(Router);
  private apiUrl = `${environment.apiUrl}Auth`;

  private currentAuthorizerSubject = new BehaviorSubject<AuthorizerResponseDto | null>(null);
  public currentAuthorizer$ = this.currentAuthorizerSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor() {
    this.checkAuthStatus();
  }

  private checkAuthStatus(): void {
    const token = this.getToken();
    const authorizer = this.getAuthorizer();
    if (token && authorizer) {
      this.currentAuthorizerSubject.next(authorizer);
      this.isAuthenticatedSubject.next(true);
    }
  }

  login(credentials: LoginRequest): Observable<ApiResponse<AuthResponse>> {
    return this.httpClient
      .post<ApiResponse<AuthResponse>>(`${this.apiUrl}/login`, credentials)
      .pipe(
        tap((response) => {
          if (response.isSuccess && response.data) {
            this.setToken(response.data.token);
            this.setRefreshToken(response.data.refreshToken);
            this.setAuthorizer(response.data.authorizer);
            this.currentAuthorizerSubject.next(response.data.authorizer);
            this.isAuthenticatedSubject.next(true);
          }
        })
      );
  }

  register(registerData: RegisterRequest): Observable<ApiResponse<AuthorizerResponseDto>> {
    return this.httpClient.post<ApiResponse<AuthorizerResponseDto>>(
      `${this.apiUrl}/register`,
      registerData
    );
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('authorizer');
    localStorage.removeItem('selected_tenant');
    this.currentAuthorizerSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private setToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  private setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  }

  getAuthorizer(): AuthorizerResponseDto | null {
    const authorizerJson = localStorage.getItem('authorizer');
    return authorizerJson ? JSON.parse(authorizerJson) : null;
  }

  private setAuthorizer(authorizer: AuthorizerResponseDto): void {
    localStorage.setItem('authorizer', JSON.stringify(authorizer));
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getCurrentAuthorizer(): AuthorizerResponseDto | null {
    return this.currentAuthorizerSubject.value;
  }

  isAdminOrUser(): boolean {
    const role = this.getRole();

    if (!role) return false;

    return (
      role.toLowerCase() === 'admin' ||
      role.toLowerCase() === 'superadmin' ||
      role.toLowerCase() === 'user'
    );
  }

  isAdmin(): boolean {
    const role = this.getRole();
    if (!role) return false;
    return role.toLowerCase() === 'admin' || role.toLowerCase() === 'superadmin';
  }

  canDelete(): boolean {
    return this.isAdmin();
  }

  getRole(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      return payload.role || null;
    } catch (error) {
      return null;
    }
  }

  getSelectedTenant(): any | null {
    const tenantJson = localStorage.getItem('selected_tenant');
    return tenantJson ? JSON.parse(tenantJson) : null;
  }

  getTenantId(): string | null {
    const tenant = this.getSelectedTenant();
    return tenant?.id ?? null;
  }

  setSelectedTenant(tenant: any): void {
    localStorage.setItem('selected_tenant', JSON.stringify(tenant));
  }

  verifyEmail(token: string): Observable<ApiResponse<string>> {
    return this.httpClient.get<ApiResponse<string>>(`${this.apiUrl}/verify-email?token=${token}`);
  }

  resendVerificationEmail(email: string): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(`${this.apiUrl}/resend-verification`, {
      email,
    });
  }

  requestPasswordReset(email: string): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(`${this.apiUrl}/request-password-reset`, {
      email,
    });
  }

  resetPassword(token: string, newPassword: string): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(`${this.apiUrl}/reset-password`, {
      token,
      newPassword,
    });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(`${this.apiUrl}/change-password`, {
      currentPassword,
      newPassword,
    });
  }

  forceChangePassword(
    userId: number,
    currentPassword: string,
    newPassword: string
  ): Observable<ApiResponse<AuthorizerResponseDto>> {
    return this.httpClient
      .post<ApiResponse<AuthorizerResponseDto>>(`${this.apiUrl}/force-change-password`, {
        userId,
        currentPassword,
        newPassword,
      })
      .pipe(
        tap((response) => {
          if (response.isSuccess && response.data) {
            // Update local storage with fresh user data
            this.setAuthorizer(response.data);
            this.currentAuthorizerSubject.next(response.data);
          }
        })
      );
  }
}
